import { useState, useEffect, useRef } from 'react';
import { useUser } from '@/hooks/use-user';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormRenderer } from './form-renderer';
import { ProfileCompletionStep } from './profile-completion-step';
import type {
  User,
  OnboardingFormConfig,
  FormAnswerValue,
  ProfileFieldConfig,
  UserWithCommunities,
} from '@/types';
import axiosInstance from '@/lib/axios';
import {
  buildRichOnboardingAnswers,
  getFormValueFromStoredOnboarding,
  sanitizeOnboardingAnswers,
} from '@/lib/onboarding-answers';
import { toast } from 'sonner';
import {
  filterOnboardingFormForMembershipStatus,
  isPreApprovalMembershipStatus,
} from '@/lib/onboarding-form-for-membership';

type CommunityRow = UserWithCommunities['communities'][number];

function missingRequiredProfileFieldKeys(c: CommunityRow, user: User): string[] {
  const profileConfig = c.profileFieldConfig as ProfileFieldConfig | undefined;
  const missing: string[] = [];
  if (!profileConfig) return missing;
  if (profileConfig.image?.enabled && profileConfig.image?.required && !user.image)
    missing.push('image');
  if (profileConfig.dob?.enabled && profileConfig.dob?.required && !user.dob) missing.push('dob');
  if (
    profileConfig.anniversaryDate?.enabled &&
    profileConfig.anniversaryDate?.required &&
    !user.anniversaryDate
  ) {
    missing.push('anniversaryDate');
  }
  if (profileConfig.gender?.enabled && profileConfig.gender?.required && !user.gender)
    missing.push('gender');
  if (profileConfig.bloodGroup?.enabled && profileConfig.bloodGroup?.required && !user.bloodGroup)
    missing.push('bloodGroup');
  if (
    profileConfig.address?.enabled &&
    profileConfig.address?.required &&
    (!user.address ||
      !user.address.line1 ||
      !user.address.city ||
      !user.address.state ||
      !user.address.country ||
      !user.address.pincode)
  ) {
    missing.push('address');
  }
  if (
    profileConfig.aadhaarCard?.enabled &&
    profileConfig.aadhaarCard?.required &&
    !user.aadhaarCardUrl
  ) {
    missing.push('aadhaarCard');
  }
  return missing;
}

export function OnboardingPrompt() {
  const { user, isLoading, mutate } = useUser();
  const location = useLocation();
  const [targetCommunity, setTargetCommunity] = useState<CommunityRow | null>(null);
  const [missingProfileFields, setMissingProfileFields] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * Cancelling / closing the dialog pauses auto-popup for this community until the user:
   * taps “Continue”, saves, or switches away in the router (webview-friendly recovery).
   */
  const [pausedOnboardingCommunityId, setPausedOnboardingCommunityId] = useState<string | null>(
    null
  );
  const previousRouteCommunityIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !user) return;

    const pathMatch = location.pathname.match(/^\/([^/]+)/);
    const currentCommunityId = pathMatch ? pathMatch[1] : null;

    if (previousRouteCommunityIdRef.current !== currentCommunityId) {
      setPausedOnboardingCommunityId(null);
      previousRouteCommunityIdRef.current = currentCommunityId;
    }

    if (!currentCommunityId) {
      setTargetCommunity(null);
      setIsOpen(false);
      return;
    }

    const communityToOnboard = user.communities.find((c) => {
      if (c.id !== currentCommunityId) return false;

      if (c.status !== 'JOINED' && c.status !== 'PENDING' && c.status !== 'INVITED') {
        return false;
      }

      if (missingRequiredProfileFieldKeys(c, user).length > 0) {
        return true;
      }

      const formConfigFull = c.onboardingForm as OnboardingFormConfig;
      const formConfigForChecks =
        filterOnboardingFormForMembershipStatus(formConfigFull, c.status) ?? formConfigFull;

      const hasEnabledForm = Boolean(
        c.onboardingFormEnabled && formConfigFull?.steps && formConfigFull.steps.length > 0
      );
      const hasApplicableFields =
        (formConfigForChecks.steps?.length ?? 0) > 0 ||
        (!isPreApprovalMembershipStatus(c.status) && hasEnabledForm);

      if (!hasEnabledForm || !hasApplicableFields) return false;

      if (c.isOnboarded) return false;

      const previousAnswers = c.metadata?.onboardingAnswers || {};
      let hasMissingNewField = false;

      for (const step of formConfigForChecks.steps || []) {
        for (const field of step.fields || []) {
          const val = getFormValueFromStoredOnboarding(previousAnswers[field.id]);

          if (field.required) {
            if (
              val === undefined ||
              val === '' ||
              val === null ||
              (Array.isArray(val) && val.length === 0)
            ) {
              hasMissingNewField = true;
              break;
            }
          } else if (!(field.id in previousAnswers)) {
            hasMissingNewField = true;
            break;
          }
        }
        if (hasMissingNewField) break;
      }

      if (!hasMissingNewField) {
        if (c.status === 'JOINED') {
          axiosInstance
            .patch(`/api/communities/${c.id}/membership`, {
              onboardingAnswers: buildRichOnboardingAnswers(
                formConfigFull,
                sanitizeOnboardingAnswers(previousAnswers)
              ),
            })
            .then(() => mutate())
            .catch(console.error);
        }

        return false;
      }

      return hasMissingNewField;
    });

    if (communityToOnboard) {
      const missing = missingRequiredProfileFieldKeys(communityToOnboard, user);
      const shouldPausePopup = pausedOnboardingCommunityId === communityToOnboard.id;

      setTargetCommunity(communityToOnboard);
      setMissingProfileFields(missing);

      if (!shouldPausePopup) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    } else {
      setTargetCommunity(null);
      setIsOpen(false);
      setMissingProfileFields([]);
      setPausedOnboardingCommunityId(null);
    }
  }, [user, isLoading, location.pathname, mutate, pausedOnboardingCommunityId]);

  const pauseOnboardingForCurrentCommunity = () => {
    if (targetCommunity) setPausedOnboardingCommunityId(targetCommunity.id);
  };

  const handleSubmit = async (answers: Record<string, FormAnswerValue>) => {
    if (!targetCommunity) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.patch(`/api/communities/${targetCommunity.id}/membership`, {
        onboardingAnswers: buildRichOnboardingAnswers(
          targetCommunity.onboardingForm as OnboardingFormConfig,
          sanitizeOnboardingAnswers(answers)
        ),
      });

      toast.success(
        targetCommunity.status === 'JOINED'
          ? 'Your additional details were saved.'
          : 'Saved. You may be asked for more once your membership is approved.'
      );
      setPausedOnboardingCommunityId(null);
      setIsOpen(false);

      await mutate();
    } catch (error) {
      console.error(error);
      toast.error('Could not save your details. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileComplete = async () => {
    await mutate();
  };

  const onboardingFormFiltered = targetCommunity
    ? filterOnboardingFormForMembershipStatus(
        targetCommunity.onboardingForm as OnboardingFormConfig,
        targetCommunity.status
      )
    : null;

  const config = onboardingFormFiltered ?? ({ steps: [] } as OnboardingFormConfig);
  const showProfileStep = missingProfileFields.length > 0;
  const showResumeBar =
    Boolean(targetCommunity) && pausedOnboardingCommunityId === targetCommunity?.id && !isOpen;

  return (
    <>
      {showResumeBar && targetCommunity ? (
        <div
          className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80"
          role="status"
        >
          <div className="mx-auto flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="text-sm text-foreground">
              <span className="font-semibold">{targetCommunity.name}</span>
              {' — '}
              {targetCommunity.status === 'PENDING' || targetCommunity.status === 'INVITED'
                ? 'Complete your membership details to support your join request.'
                : 'Finish your membership details when you’re ready.'}
            </p>
            <Button
              type="button"
              size="sm"
              className="shrink-0 sm:min-w-[7rem]"
              onClick={() => {
                setPausedOnboardingCommunityId(null);
                setIsOpen(true);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : null}

      {targetCommunity ? (
        <Dialog
          open={isOpen}
          onOpenChange={(open) => {
            if (!open && !isSubmitting) {
              setIsOpen(false);
              pauseOnboardingForCurrentCommunity();
            }
          }}
        >
          <DialogContent className="max-w-xl gap-0 overflow-hidden bg-background p-0 sm:max-h-[85vh]">
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-2xl font-bold text-transparent">
                One more thing...
              </DialogTitle>
              <DialogDescription>
                {targetCommunity.name}{' '}
                {showProfileStep && user
                  ? 'needs some profile details from you to verify your membership.'
                  : 'needs a few additional details from you to verify your membership.'}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto p-6 pt-2">
              {showProfileStep && user ? (
                <ProfileCompletionStep
                  user={user}
                  missingFields={missingProfileFields}
                  onComplete={handleProfileComplete}
                  onCancel={() => {
                    setIsOpen(false);
                    pauseOnboardingForCurrentCommunity();
                  }}
                />
              ) : (
                config && (
                  <FormRenderer
                    config={config}
                    onSubmit={handleSubmit}
                    onCancel={() => {
                      setIsOpen(false);
                      pauseOnboardingForCurrentCommunity();
                    }}
                    isSubmitting={isSubmitting}
                    initialAnswers={targetCommunity.metadata?.onboardingAnswers || {}}
                  />
                )
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
